export type OfflineShellStatus =
  | { readonly state: 'unsupported' | 'installing' | 'error' | 'local'; readonly error?: string }
  | { readonly state: 'caching'; readonly assets: number; readonly cached: number; readonly version: string }
  | { readonly state: 'ready'; readonly assets: number; readonly cached?: number; readonly version: string };

function activation(worker: ServiceWorker): Promise<void> {
  if (worker.state === 'activated') return Promise.resolve();
  return new Promise((resolve, reject) => worker.addEventListener('statechange', () => {
    if (worker.state === 'activated') resolve();
    else if (worker.state === 'redundant') reject(new Error('The offline cache could not be installed.'));
  }));
}

export function installOfflineShell(report: (status: OfflineShellStatus) => void = status => {
  window.dispatchEvent(new CustomEvent('dissidium:offline', { detail: status }));
}): void {
  // Service workers cannot register on file:// (the desktop shell). There the
  // game files already live on the device, so there is no copy to install.
  if (window.location.protocol === 'file:') { report({ state: 'local' }); return; }
  if (!('serviceWorker' in navigator)) { report({ state: 'unsupported' }); return; }
  report({ state: 'installing' });
  void navigator.serviceWorker.register(new URL('./shell-offline-sw.js', import.meta.url), { scope: './' })
    .then(async registration => {
      const pending = registration.installing ?? registration.waiting;
      if (pending) { pending.postMessage({ type: 'SKIP_WAITING' }); await activation(pending); }
      registration = await navigator.serviceWorker.ready;
      const worker = registration.active;
      if (!worker) throw new Error('The offline worker did not activate.');
      const ask = (type: string, timeout: number) => new Promise<OfflineShellStatus>((resolve, reject) => {
        const channel = new MessageChannel();
        const timer = timeout ? window.setTimeout(() => reject(new Error('The offline cache did not answer.')), timeout) : 0;
        channel.port1.onmessage = event => { window.clearTimeout(timer); resolve(event.data as OfflineShellStatus); };
        worker.postMessage({ type }, [channel.port2]);
      });
      const first = await ask('OFFLINE_STATUS', 5000);
      report(first);
      if (first.state === 'caching') report(await ask('PRECACHE_REST', 0));
    })
    .catch(error => report({ state: 'error', error: error instanceof Error ? error.message : String(error) }));
}
